import { useState } from 'react';
import { App, Button, Form, Input } from 'antd';
import { LockOutlined, MailOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppAuth } from '../../api/appAuth';
import { clearRememberedCredentials } from '../../auth/storage';
import Logo from '../../components/Logo/Logo';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';
import '../Login/Auth.css';

type EmailForm = {
    email: string;
};

type OtpForm = {
    otp: string;
    password: string;
    confirmPassword: string;
};

type AuthApiPayload = {
    success?: boolean;
    message?: string;
};

function isSuccessResponse(response: { status: number; data?: AuthApiPayload }) {
    const httpOk = response.status === 200 || response.status === 201;
    return httpOk && response.data?.success !== false;
}

function getApiErrorMessage(error: unknown, fallback: string) {
    if (
        error &&
        typeof error === 'object' &&
        'message' in error &&
        typeof error.message === 'string' &&
        error.message
    ) {
        return error.message;
    }

    if (error instanceof Error) {
        return error.message;
    }

    return fallback;
}

function ResetPasswordPage() {
    const { t } = useTranslation('auth');
    const { notification } = App.useApp();
    const navigate = useNavigate();
    const [emailForm] = Form.useForm<EmailForm>();
    const [otpForm] = Form.useForm<OtpForm>();
    const [step, setStep] = useState<'email' | 'otp'>('email');
    const [email, setEmail] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const onRequestOtp = async (values: EmailForm) => {
        setSubmitting(true);

        try {
            const response = await AppAuth.ForgotPassword({
                email: values.email.trim(),
            });

            if (!isSuccessResponse(response)) {
                notification.error({
                    message: t('errors.sendOtp'),
                    description: getApiErrorMessage(response.data, t('errors.sendOtp')),
                });
                return;
            }

            setEmail(values.email.trim());
            setStep('otp');
            otpForm.resetFields();
            notification.success({
                message: t('otp.sent'),
                description: t('otp.sentTo', { email: values.email.trim() }),
            });
        } catch (error) {
            notification.error({
                message: t('errors.sendOtp'),
                description: getApiErrorMessage(error, t('errors.sendOtp')),
            });
        } finally {
            setSubmitting(false);
        }
    };

    const onVerifyOtp = async ({ otp, password }: OtpForm) => {
        if (!email) {
            setStep('email');
            return;
        }

        setSubmitting(true);

        try {
            const response = await AppAuth.ResetPassword({
                email,
                otp,
                password,
            });

            if (!isSuccessResponse(response)) {
                notification.error({
                    message: t('errors.resetPassword'),
                    description: getApiErrorMessage(response.data, t('errors.resetPassword')),
                });
                return;
            }

            clearRememberedCredentials();
            notification.success({
                message: t('reset.success'),
                description: t('reset.successDescription'),
            });
            navigate('/login', { replace: true });
        } catch (error) {
            notification.error({
                message: t('errors.resetPassword'),
                description: getApiErrorMessage(error, t('errors.correctOtp')),
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="auth">
            <div className="auth__card">
                <div className="auth__language">
                    <LanguageSwitcher compact />
                </div>
                <div className="auth__brand">
                    <Logo />
                </div>

                {step === 'email' ? (
                    <>
                        <h1 className="auth__title">{t('reset.title')}</h1>
                        <p className="auth__subtitle">{t('reset.subtitle')}</p>

                        <Form
                            form={emailForm}
                            layout="vertical"
                            onFinish={onRequestOtp}
                            initialValues={email ? { email } : undefined}
                        >
                            <Form.Item
                                name="email"
                                label={t('fields.email')}
                                rules={[
                                    { required: true, message: t('validation.emailRequired') },
                                    { type: 'email', message: t('validation.emailInvalid') },
                                ]}
                            >
                                <Input
                                    prefix={<MailOutlined />}
                                    placeholder={t('fields.email')}
                                    autoComplete="email"
                                />
                            </Form.Item>

                            <Button type="primary" htmlType="submit" loading={submitting}>
                                {t('reset.sendOtp')}
                            </Button>
                        </Form>
                    </>
                ) : (
                    <>
                        <h1 className="auth__title">{t('otp.title')}</h1>
                        <p className="auth__subtitle">{t('reset.otpSubtitle', { email })}</p>

                        <Form form={otpForm} layout="vertical" onFinish={onVerifyOtp}>
                            <Form.Item
                                name="otp"
                                className="auth__otp-item"
                                rules={[
                                    { required: true, message: t('validation.otpRequired') },
                                    { len: 6, message: t('validation.otpLength') },
                                ]}
                            >
                                <Input.OTP
                                    length={6}
                                    size="large"
                                    type="text"
                                    inputMode="numeric"
                                    formatter={(value) => value.replace(/\D/g, '')}
                                    disabled={submitting}
                                />
                            </Form.Item>

                            <Form.Item
                                name="password"
                                label={t('fields.newPassword')}
                                rules={[
                                    { required: true, message: t('validation.passwordRequired') },
                                    { min: 6, message: t('validation.passwordMin') },
                                ]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined />}
                                    placeholder={t('fields.newPassword')}
                                    autoComplete="new-password"
                                />
                            </Form.Item>

                            <Form.Item
                                name="confirmPassword"
                                label={t('fields.confirmPassword')}
                                dependencies={['password']}
                                rules={[
                                    { required: true, message: t('validation.confirmPassword') },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue('password') === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(
                                                new Error(t('validation.passwordMismatch'))
                                            );
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined />}
                                    placeholder={t('fields.confirmPassword')}
                                    autoComplete="new-password"
                                />
                            </Form.Item>

                            <Button type="primary" htmlType="submit" loading={submitting}>
                                {t('reset.submit')}
                            </Button>
                        </Form>

                        <button
                            type="button"
                            className="auth__back"
                            disabled={submitting}
                            onClick={() => setStep('email')}
                        >
                            {t('reset.back')}
                        </button>
                    </>
                )}

                <p className="auth__footer">
                    {t('reset.remember')} <Link to="/login">{t('login.submit')}</Link>
                </p>
            </div>
        </div>
    );
}

export default ResetPasswordPage;
